import React from "react";
import {
  ArrowUpRight,
  BookOpen,
  Clock,
  DoorOpen,
  GraduationCap,
  Monitor,
  Users,
} from "lucide-react";
import "./TpcHostedSessions.css";
import hostedTheoryRoomImage from "../../images/Landscape/MCI2.webp";

const hostedSessionHighlights = [
  {
    id: "rooms",
    label: "Hosted Rooms",
    value: "2 Live Theory Rooms",
    icon: DoorOpen,
    trend: "gold",
  },
  {
    id: "format",
    label: "Session Format",
    value: "Mentor-Led Classes",
    icon: GraduationCap,
    trend: "green",
  },
  {
    id: "transition",
    label: "Next Step",
    value: "Straight Onto The Floors",
    icon: Users,
    trend: "red",
  },
];

const hostedSessionFlow = [
  {
    id: "brief",
    step: "01",
    title: "Brief the market",
    body: "Students learn the structure, key levels and price-action story before any execution decision is made.",
    trend: "green",
  },
  {
    id: "challenge",
    step: "02",
    title: "Challenge the plan",
    body: "Trade mentors stress-test entries, invalidation, risk and trade management in a live classroom setting.",
    trend: "gold",
  },
  {
    id: "apply",
    step: "03",
    title: "Move to the floors",
    body: "After the hosted session, students step into the Trade Floors to apply the lesson while the market context is fresh.",
    trend: "red",
  },
];

const marketCandles = [
  "green",
  "gold",
  "red",
  "green",
  "green",
  "red",
  "gold",
  "green",
  "red",
  "green",
  "green",
  "gold",
];

function TpcHostedSessions() {
  return (
    <section
      id="tpc-hosted-sessions"
      className="trpc-tpcHostedSessions"
      aria-labelledby="trpc-tpcHostedSessions-title"
    >
      <div className="trpc-tpcHostedSessions-shell">
        <div className="trpc-tpcHostedSessions-grid">
          <div className="trpc-tpcHostedSessions-visual">
            <div
              className="trpc-tpcHostedSessions-marketLayer"
              aria-hidden="true"
            >
              <span className="trpc-tpcHostedSessions-priceLine trpc-tpcHostedSessions-priceLineOne" />
              <span className="trpc-tpcHostedSessions-priceLine trpc-tpcHostedSessions-priceLineTwo" />

              <div className="trpc-tpcHostedSessions-candleGroup">
                {marketCandles.map((trend, index) => (
                  <span
                    key={`${trend}-${index}`}
                    className="trpc-tpcHostedSessions-candle"
                    data-trend={trend}
                  />
                ))}
              </div>
            </div>

            <figure className="trpc-tpcHostedSessions-photoFrame">
              <div
                className="trpc-tpcHostedSessions-frameTop"
                aria-hidden="true"
              >
                <span />
                <span />
                <span />
              </div>

              <img
                className="trpc-tpcHostedSessions-photo"
                src={hostedTheoryRoomImage}
                alt="TPC hosted trade theory room with two trade instructors leading a live session"
                loading="lazy"
                decoding="async"
              />

              <span
                className="trpc-tpcHostedSessions-photoVignette"
                aria-hidden="true"
              />

              <figcaption className="trpc-tpcHostedSessions-photoCaption">
                <span className="trpc-tpcHostedSessions-captionEyebrow">
                  Hosted Session Live
                </span>
                <strong>Trade theory room briefing</strong>
                <span>
                  Two instructors lead the room through price action, execution
                  rules and risk before traders move onto the floor.
                </span>
              </figcaption>

              <div
                className="trpc-tpcHostedSessions-routeBadge"
                aria-hidden="true"
              >
                <span>Theory Room</span>
                <ArrowUpRight size={16} strokeWidth={2.5} />
                <span>Trade Floor</span>
              </div>

              <div
                className="trpc-tpcHostedSessions-roomCounter"
                aria-hidden="true"
              >
                <strong>2</strong>
                <span>Hosted Rooms</span>
              </div>
            </figure>
          </div>

          <div className="trpc-tpcHostedSessions-copy">
            <div
              className="trpc-tpcHostedSessions-terminalTop"
              aria-hidden="true"
            >
              <span />
              <span />
              <span />
            </div>

            <span className="trpc-tpcHostedSessions-kicker">
              Trade Floors · Hosted Sessions
            </span>

            <div className="trpc-tpcHostedSessions-intro">
              <span className="trpc-tpcHostedSessions-eyebrow">
                Campus Briefing Rooms
              </span>

              <h2
                id="trpc-tpcHostedSessions-title"
                className="trpc-tpcHostedSessions-title"
              >
                Start in theory. Step onto the floor with a plan.
              </h2>

              <p className="trpc-tpcHostedSessions-lead">
                Before students enter the Trade Floors, they sharpen the “why”
                behind every decision inside our hosted trade theory rooms.
                These sessions turn market structure, price action and risk
                into a shared language the room can take straight onto the desk.
              </p>
            </div>

            <dl
              className="trpc-tpcHostedSessions-miniFacts"
              aria-label="Hosted session details"
            >
              {hostedSessionHighlights.map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.id}
                    className="trpc-tpcHostedSessions-miniFact"
                    data-trend={item.trend}
                  >
                    <span
                      className="trpc-tpcHostedSessions-miniFactIcon"
                      aria-hidden="true"
                    >
                      <Icon size={20} strokeWidth={2.35} />
                    </span>

                    <div className="trpc-tpcHostedSessions-miniFactCopy">
                      <dt>{item.label}</dt>
                      <dd>{item.value}</dd>
                    </div>
                  </div>
                );
              })}
            </dl>

            <ol className="trpc-tpcHostedSessions-flow">
              {hostedSessionFlow.map((item) => (
                <li
                  key={item.id}
                  className="trpc-tpcHostedSessions-flowStep"
                  data-trend={item.trend}
                >
                  <span className="trpc-tpcHostedSessions-flowIndex">
                    {item.step}
                  </span>

                  <div className="trpc-tpcHostedSessions-flowCopy">
                    <h3>{item.title}</h3>
                    <p>{item.body}</p>
                  </div>
                </li>
              ))}
            </ol>

            <div
              className="trpc-tpcHostedSessions-ctaRow"
              aria-label="Hosted session actions"
            >
              <a
                className="trpc-tpcHostedSessions-btn trpc-tpcHostedSessions-btnPrimary"
                href="#tpc-trade-floors"
                data-candle="green"
              >
                <span>Explore The Trade Floors</span>
                <ArrowUpRight size={17} strokeWidth={2.4} aria-hidden="true" />
              </a>

              <a
                className="trpc-tpcHostedSessions-btn trpc-tpcHostedSessions-btnSecondary"
                href="https://andus.co.za/trade-register"
                target="_blank"
                rel="noopener noreferrer"
                data-candle="red"
              >
                <span>Apply For Bootcamp</span>
                <ArrowUpRight size={17} strokeWidth={2.4} aria-hidden="true" />
              </a>
            </div>

            <div className="trpc-tpcHostedSessions-sessionStrip">
              <span>
                <BookOpen size={16} strokeWidth={2.4} aria-hidden="true" />
                Theory
              </span>
              <span>
                <Monitor size={16} strokeWidth={2.4} aria-hidden="true" />
                Live Context
              </span>
              <span>
                <Clock size={16} strokeWidth={2.4} aria-hidden="true" />
                Floor Ready
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default TpcHostedSessions;